import { createContext, useContext, ReactNode, useMemo } from 'react'

export const ROLE = {
  CUSTOMER: 'customer',
  STYLE_ADVISOR: 'style-advisor',
} as const

export type Role = (typeof ROLE)[keyof typeof ROLE]

// Tracking context for analytics
export interface TrackingContext {
  storeId?: string
  employeeId?: string
  userId?: string
  sessionId?: string
}

interface CustomerHubContextType {
  role?: Role
  context?: string
  tracking?: TrackingContext
  disableAnimations?: boolean
}

const CustomerHubContext = createContext<CustomerHubContextType | undefined>(
  undefined,
)

export function CustomerHubProvider({
  children,
  role,
  context,
  tracking,
  disableAnimations = false,
}: {
  children: ReactNode
  role?: Role
  context?: string
  tracking?: TrackingContext
  disableAnimations?: boolean
}) {
  const value = useMemo(
    () => ({ role, context, tracking, disableAnimations }),
    [role, context, tracking, disableAnimations],
  )

  return (
    <CustomerHubContext.Provider value={value}>
      {children}
    </CustomerHubContext.Provider>
  )
}

export function useCustomerHub() {
  const contextValue = useContext(CustomerHubContext)
  if (!contextValue) {
    throw new Error('useCustomerHub must be used within a CustomerHubProvider')
  }
  return contextValue
}
